import {
  AssessmentSectionSubjectVisibility,
  AssessmentType,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";

import AssessmentSectionSubjectVisibilityBanner from "../assessment/assessment-section-subject-visibility-banner";
import AssessmentLayout from "../layouts/assessment-layout";
import AssessmentQuestionItem from "../questions/assessment-question-item";
import {
  AssessmentAnswer,
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestionContainer,
} from "../questions/types";
import { OverallCoreValuesAssessmentQuestionDescriptionContext } from "./overall-core-values-question-context";

const CoreValueBasedQuestions = ({
  assessmentType,
  individualCoreValueBasedQuestions,
  overallCoreValueBasedQuestion,
  proposedAnswers,
  formDisabled,
  showErrors,
  otherParticipantsAnswers,
  periodStartDate,
  periodEndDate,
  target,
  subjectVisibility,
  onUpdateAnswer,
}: {
  assessmentType: AssessmentType;
  overallCoreValueBasedQuestion?: AssessmentQuestionContainer;
  individualCoreValueBasedQuestions: AssessmentQuestionContainer[];
  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  proposedAnswers: AssessmentAnswer[];
  formDisabled: boolean;
  showErrors: boolean;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  target?: BasicUser | null;
  subjectVisibility: AssessmentSectionSubjectVisibility;
  onUpdateAnswer: (answer: AssessmentAnswer) => void;
}) => {
  const label = useLabel();
  const overallCoreValueBasedQuestionAnswer = proposedAnswers.find(
    (answer) =>
      overallCoreValueBasedQuestion &&
      answer.questionId === overallCoreValueBasedQuestion.id,
  );
  const sumAnswersOfIndividualCoreValueBasedQuestions =
    individualCoreValueBasedQuestions.reduce((sum, question) => {
      const answer = proposedAnswers.find(
        (answer) => answer.questionId === question.id,
      );
      if (!answer || !answer.integerAnswer) return sum;
      return sum + answer.integerAnswer;
    }, 0);
  const averageScoreOfIndividualCoreValueBasedQuestions =
    individualCoreValueBasedQuestions.length === 0 ||
    sumAnswersOfIndividualCoreValueBasedQuestions === 0
      ? 0
      : sumAnswersOfIndividualCoreValueBasedQuestions /
        individualCoreValueBasedQuestions.length;

  const currentUser = currentUserVar();
  return (
    <>
      <AssessmentLayout.SectionHeading>
        <AssessmentLayout.SectionHeadingTitle>
          Core Values
        </AssessmentLayout.SectionHeadingTitle>
        <AssessmentLayout.SectionHeadingDescription>
          Assess alignment with Core Values in this {label("review")} cycle
        </AssessmentLayout.SectionHeadingDescription>
        {target && target?.id !== currentUser.id && (
          <AssessmentSectionSubjectVisibilityBanner
            subjectVisibility={subjectVisibility}
            className="mt-2 mb-4"
          />
        )}
      </AssessmentLayout.SectionHeading>

      <AssessmentLayout.SectionRegularQuestions>
        {individualCoreValueBasedQuestions.length === 0 && (
          <div className="p-6">
            <div className="text-sm text-gray-500">
              No Core Values to assess.
            </div>
          </div>
        )}
        {individualCoreValueBasedQuestions.map((questionWithWeight) => {
          const { question } = questionWithWeight;
          const answer = proposedAnswers.find(
            (answer) => answer.questionId === question.id,
          );
          return (
            <AssessmentQuestionItem
              key={question.id}
              assessmentType={assessmentType}
              otherParticipantsAnswers={otherParticipantsAnswers?.filter(
                (answer) => answer.questionId === question.id,
              )}
              answer={answer}
              question={question}
              target={target}
              onUpdateAnswer={onUpdateAnswer}
              formDisabled={formDisabled}
              showError={showErrors}
              periodStartDate={periodStartDate}
              periodEndDate={periodEndDate}
            />
          );
        })}
        {overallCoreValueBasedQuestion && (
          <div>
            <OverallCoreValuesAssessmentQuestionDescriptionContext.Provider
              value={{
                averageScoreOfIndividualCoreValuesBasedQuestions: String(
                  averageScoreOfIndividualCoreValueBasedQuestions.toFixed(1),
                ).replace(".0", ""),
                scale:
                  overallCoreValueBasedQuestion.question.__typename ===
                  "OverallRecognitionCoreValueAssessmentQuestionNode"
                    ? overallCoreValueBasedQuestion.question.labels.length
                    : undefined,
              }}
            >
              <AssessmentQuestionItem
                key={overallCoreValueBasedQuestion.id}
                assessmentType={assessmentType}
                otherParticipantsAnswers={otherParticipantsAnswers?.filter(
                  (answer) =>
                    answer.questionId === overallCoreValueBasedQuestion.id,
                )}
                answer={overallCoreValueBasedQuestionAnswer}
                target={target}
                question={overallCoreValueBasedQuestion.question}
                onUpdateAnswer={onUpdateAnswer}
                formDisabled={formDisabled}
                showError={showErrors}
              />
            </OverallCoreValuesAssessmentQuestionDescriptionContext.Provider>
          </div>
        )}
      </AssessmentLayout.SectionRegularQuestions>
    </>
  );
};

export default CoreValueBasedQuestions;
